import cloneDeep from 'lodash/cloneDeep';
import { DEALS_LANDING_PAGE_FETCHED, DEAL_LOADED } from '../deals/actionTypes';
import {
	categoryToRemove,
	getPagination,
	getSearchFallback,
	moveFilterToPillArea,
} from './helpers';

import initialState from './initialState';

export * from './actions';

export const DEFAULT_ALL_RADIO_OPTION_ID = 'DEFAULT_ALL_RADIO_OPTION_ID';

/** @internal */
export const allOptions = {
	id: DEFAULT_ALL_RADIO_OPTION_ID,
	name: 'All',
	display: {
		patternsIconClass: '',
	},
	singular: {
		slug: 'All',
		term: 'All',
	},
	plural: {
		slug: 'All',
		term: 'All',
	},
};

const handleTransferFilters = (
	pillLimit: Search.FilterPillLimit,
	needToDropPill: boolean,
	stateCopy: Redux.Search,
) => {
	if (pillLimit < 6 && needToDropPill) {
		while (stateCopy.filterPills.categories.length > pillLimit - 1) {
			const [category, categoryIndex] = categoryToRemove(
				stateCopy.filterPills.categories,
			);
			stateCopy.filterPills.categories.splice(categoryIndex, 1);
			stateCopy.filterPills.sidebar.categories.unshift(category);
		}
	} else if (pillLimit > stateCopy.filterPills.limit) {
		while (
			stateCopy.filterPills.categories.length < pillLimit - 1 &&
			stateCopy.filterPills.sidebar.categories.length
		) {
			moveFilterToPillArea(stateCopy);
		}
	}
};

export default function searchReducer(
	state = initialState,
	action: Search.ActionTypes,
) {
	const stateCopy = cloneDeep(state);
	switch (action.type) {
		case 'FETCH_SEARCH_RESULTS':
			stateCopy.isFetching = true;
			stateCopy.selectedCategories = null;
			break;
		case 'FETCH_SEARCH_RESULTS_SUCCESS': {
			const { data } = action.payload;
			stateCopy.boundingBox = data.search.boundingBox || null;
			stateCopy.isFetching = false;
			stateCopy.searchFallback = getSearchFallback(data.searchFallback);
			stateCopy.pagination = getPagination(data.search.pagination);
			stateCopy.vendors = data.search.profiles;
			stateCopy.seed = action.payload.seed;
			stateCopy.sort = data.search.sort.type;
			stateCopy.supplemental = data.search.supplemental;
			stateCopy.total = data.search.total.profiles;
			stateCopy.sortVariation = data.search.sort.variation;
			stateCopy.sortVersion = data.search.sort.version;
			stateCopy.scoreType = data.search.sort.scoreType;
			stateCopy.isPermitted = data.isPermitted ? data.isPermitted.flag : true; // not required in FE calls
			stateCopy.searchCopy = data.searchCopy?.html || '';
			stateCopy.filterPills.deiFilters = data.search.deiFilters || [];
			stateCopy.marketCode = data.search.marketCode || '';
			stateCopy.priceRange = data.search.priceRange || null;
			stateCopy.selectedCategories = data.search.selectedCategories ?? null;
			break;
		}
		case 'FETCH_SEARCH_RESULTS_ERROR': {
			stateCopy.isFetching = false;
			stateCopy.vendors = [] as Vendor.Raw[];
			stateCopy.supplemental = [] as Vendor.Raw[];
			const errors = action.payload.errors || [];
			stateCopy.errorMessage = errors[0]?.message;
			stateCopy.filterPills.deiFilters = [];
			stateCopy.selectedCategories = null;
			// Clear result-derived fields set by FETCH_SEARCH_RESULTS_SUCCESS so a
			// failed fetch can't leave a previous view's marketCode/total/page
			// count behind. Consumers (e.g. useVendorCategoryViewedTracking) read
			// marketCode as the "this view's results are loaded" gate; without
			// this, it stays truthy from the last successful fetch and reads as
			// ready even though this fetch errored. pagination.count similarly
			// drives pagination UI (e.g. SearchResults' hasPagination) — leaving
			// it stale would keep pagination controls enabled for a view with 0
			// results.
			stateCopy.marketCode = '';
			stateCopy.total = 0;
			stateCopy.pagination = { ...stateCopy.pagination, count: 0 };
			break;
		}
		case 'filters/UPDATE_PAGE':
			stateCopy.pagination = {
				...state.pagination,
				page: action.payload.page,
			};
			break;

		case 'filters/UPDATE_SORT':
			stateCopy.sort = action.payload.sort;
			break;

		case 'filters/UPDATE_FOLLOWABLE_FACETS':
			stateCopy.followableFacets = action.payload.followableFacets;
			break;

		case 'filters/RECEIVE_FILTER_PILLS':
			stateCopy.filterPills.categories = action.pills;
			stateCopy.filterPills.sidebar.categories = action.sidebar;
			stateCopy.filterPills.limit = action.limit;
			break;

		case 'filters/SET_AVAILABLE_SPACE': {
			stateCopy.sizes[action.item] = action.availableSpace;
			break;
		}

		case 'SET_FILTER_PILL_LIMIT': {
			const { payload } = action;
			if (payload !== stateCopy.filterPills.limit) {
				const moreFiltersButtonExists =
					stateCopy.filterPills.sidebar.categories.length > 0;
				const pillWithDropdownCount = stateCopy.filterPills.categories.length;
				const needToDropPill = moreFiltersButtonExists
					? pillWithDropdownCount > payload - 1
					: pillWithDropdownCount > payload;

				handleTransferFilters(payload, needToDropPill, stateCopy);
				stateCopy.filterPills.limit = action.payload;
			}
			break;
		}

		case 'SET_MAX_RESULT_COLUMNS': {
			stateCopy.maxResultColumns = action.payload;
			break;
		}

		case 'semanticSearch/SET_MARKET_CODE': {
			stateCopy.marketCode = action.payload.marketCode;
			break;
		}

		case DEALS_LANDING_PAGE_FETCHED: {
			stateCopy.vendors = action.payload.vendors;
			stateCopy.total = action.payload.total;
			stateCopy.supplemental = action.payload.supplemental;
			stateCopy.pagination = getPagination(action.payload.pagination);
			stateCopy.maxResultColumns = action.payload.maxResultColumns;
			break;
		}

		case DEAL_LOADED: {
			stateCopy.dealId = action.payload;
			break;
		}

		default:
			return state;
	}
	return stateCopy;
}
